import {
    HttpMethod,
    PathEntityIdentifier,
    RequestBody,
} from 'src/@types/modules/api.types';
import {
    Group,
    GroupLearningProgress,
    QueryPayload,
    SortingData,
} from 'src/api';
import { provideTags } from 'src/api/utils/functions/provide-tags';
import { Student__Legacy } from 'src/api/endpoints/students';
import { baseApi } from 'src/api/baseApi';

const api = baseApi.injectEndpoints({
    endpoints: builder => ({
        getGroups: builder.query<
            Group[],
            {
                params: {
                    branchId: number;
                };
                search?: string;
            }
        >({
            providesTags: (result?: Group[]) =>
                result
                    ? [
                          ...result.map((group: Group) => ({
                              type: 'Group' as const,
                              id: group.id,
                          })),
                      ]
                    : ['Group'],
            query: ({ search = '', params }) => ({
                url: `/branches/${params.branchId}/groups?` + search,
                params: {
                    page: 0,
                    size: 10000,
                },
                method: 'GET',
                errorMessage: 'Не удалось получить информацию о группах!',
            }),
            transformResponse: (responseBody: any): Group[] => {
                return responseBody.content;
            },
        }),

        getGroupStudents: builder.query<
            Student__Legacy[],
            { params: { groupId: number } }
        >({
            providesTags: (r, e, { params }) => [
                { type: 'Group', id: params.groupId },
                ...provideTags('GroupStudent', r),
            ],
            query: ({ params }): QueryPayload => ({
                url: `/groups/${params.groupId}/students`,
                method: 'GET',
            }),
        }),

        createGroup: builder.mutation<
            void,
            {
                body: {
                    name: string;
                    capacity: number;
                    year: number;
                    expectedLessons: number;
                };
                params: {
                    branchId: number;
                };
            }
        >({
            invalidatesTags: ['Group'],
            query: ({ body, params }) => ({
                url: `/branches/${params.branchId}/groups`,
                method: 'POST',
                data: body,
                errorMessage: 'Не удалось создать группу!',
            }),
        }),
        disbandGroup: builder.mutation<void, { id: number }>({
            invalidatesTags: ['Group'],
            query: ({ id }) => ({
                url: `/groups/${id}/disband`,
                method: 'POST',
                errorMessage: 'Не удалось расформировать группу!',
            }),
        }),
        freezeGroup: builder.mutation<
            void,
            { id: number; startAt: number; endsAt: number; reason: string }
        >({
            invalidatesTags: ['Group'],
            query: ({ id, ...rest }) => ({
                url: `/groups/${id}/freeze`,
                method: 'POST',
                data: rest,
                errorMessage: 'Не удалось заморозить группу!',
            }),
        }),
        unfreezeGroup: builder.mutation<void, { id: number }>({
            invalidatesTags: ['Group'],
            query: ({ id }) => ({
                url: `/groups/${id}/unfreeze`,
                method: 'POST',
                errorMessage: 'Не удалось разморозить группу!',
            }),
        }),
        releaseGroup: builder.mutation<
            void,
            { id: number; releasedAt: number }
        >({
            invalidatesTags: ['Group'],
            query: ({ id, releasedAt }) => ({
                url: `/groups/${id}/release`,
                method: 'POST',
                data: { releasedAt },
                errorMessage: 'Не удалось выпустить группу!',
            }),
        }),
        getMergeMeta: builder.query<Group[], { id: number }>({
            query: ({ id }) => ({
                url: `/groups/${id}/merge`,
                method: 'GET',
                errorMessage: 'Не удалось получить информацию о группах!',
            }),
            transformResponse(val: any) {
                return val.availableTargets;
            },
        }),
        mergeGroup: builder.mutation<void, { id: number; mergeInto: number }>({
            invalidatesTags: ['Group'],
            query: ({ id, mergeInto }) => ({
                url: `/groups/${id}/merge`,
                method: 'POST',
                data: { mergeInto },
                errorMessage: 'Не удалось объединить группы!',
            }),
        }),
        getGroupSearchData: builder.query<SortingData, void>({
            query: () => ({
                url: '/groups/page',
                method: 'GET',
                errorMessage:
                    'Не удалось получить информацию о группах для сортировки!',
            }),
        }),
        getGroupById: builder.query<Group, number>({
            providesTags: (result: Group | undefined) => [
                result ? { type: 'Group' as const, id: result.id } : 'Group',
            ],
            query: id => ({
                url: `/groups/${id}`,
                method: 'GET',
                errorMessage: 'Не удалось получить информацию о группах!',
            }),
        }),
        getGroupLearningProgress: builder.query<GroupLearningProgress, number>({
            providesTags: (_result, _error, arg) => [
                { type: 'Group', id: arg },
            ],
            query: (id: number) => ({
                url: `/groups/${id}/metrics/lessons`,
                method: 'GET',
                errorMessage:
                    'Не удалось получить информацию о количестве отработанных пар!',
            }),
        }),
        updateGroup: builder.mutation<void, UpdateGroupRequestPayload>({
            invalidatesTags: (
                _result: void | undefined,
                _error: unknown,
                payload: UpdateGroupRequestPayload
            ) => [{ type: 'Group' as const, id: payload.path.groupId }],
            query: (payload: UpdateGroupRequestPayload): QueryPayload => ({
                url: `/groups/${payload.path.groupId}`,
                method: HttpMethod.Patch,
                data: payload.body,
                errorMessage: 'Не удалось обновить информацию о группе',
            }),
        }),
    }),
});

export type UpdatingGroup = {
    name: string;
    capacity: number;
    year: number;
    expectedLessons: number;
};

export type UpdateGroupRequestPayload = PathEntityIdentifier<'groupId'> &
    RequestBody<UpdatingGroup>;

export const {
    useGetGroupStudentsQuery,
    useGetGroupsQuery,
    useCreateGroupMutation,
    useUpdateGroupMutation,
    useDisbandGroupMutation,
    useFreezeGroupMutation,
    useUnfreezeGroupMutation,
    useReleaseGroupMutation,
    useGetMergeMetaQuery,
    useMergeGroupMutation,
    useGetGroupSearchDataQuery,
    useGetGroupByIdQuery,
    useGetGroupLearningProgressQuery,
} = api;
